import { useEffect, useRef, useState } from 'react';
import { useScope } from '../hooks/useScope';
import { Scope } from '../types/scope';

interface ScopeSwitcherProps {
  className?: string;
}

export function ScopeSwitcher({ className = '' }: ScopeSwitcherProps) {
  const { activeScope, availableScopes, setActiveScope } = useScope();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScopeChange = (scope: Scope) => {
    setActiveScope(scope);
    setIsOpen(false);
  };

  // Dismiss on click-outside or Escape — header dropdowns must not stay pinned.
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  if (availableScopes.length <= 1) {
    return null;
  }

  const canGroup = availableScopes.every((s) => s.operatingEntityName);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select yard"
        className="flex items-center gap-2 px-3 py-2 bg-drawer border border-border rounded-md shadow-sm text-text-primary hover:bg-hover-bg focus:outline-none focus:ring-2 focus:ring-accent-blue"
        style={{ backgroundColor: 'var(--bg-drawer)' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted shrink-0">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        {activeScope ? (
          <span className="text-sm font-medium">
            {activeScope.name || activeScope.facilityId}
          </span>
        ) : (
          <span className="text-sm text-text-secondary">Select Yard</span>
        )}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`text-text-muted shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute right-0 z-[1000] mt-2 w-64 bg-drawer border border-border rounded-md shadow-lg max-h-96 overflow-y-auto"
          style={{ backgroundColor: 'var(--bg-drawer)' }}
        >
          {availableScopes.length === 0 ? (
            <div className="p-4 text-sm text-text-secondary" style={{ backgroundColor: 'var(--bg-drawer)' }}>No scopes available</div>
          ) : canGroup ? (
            Object.entries(
              availableScopes.reduce((acc, s) => {
                const key = s.operatingEntityName!;
                if (!acc[key]) acc[key] = [];
                acc[key].push(s);
                return acc;
              }, {} as Record<string, Scope[]>)
            ).map(([entityName, scopes]) => (
              <div key={entityName}>
                <div className="px-4 py-1 text-xs font-semibold text-text-secondary uppercase tracking-wider bg-drawer border-b border-border sticky top-0">
                  {entityName}
                </div>
                {scopes.map((scope) => (
                  <button
                    key={scope.facilityId}
                    role="option"
                    aria-selected={activeScope?.facilityId === scope.facilityId}
                    onClick={() => handleScopeChange(scope)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-hover-bg ${
                      activeScope?.facilityId === scope.facilityId
                        ? 'bg-accent-blue/10 text-accent-blue'
                        : 'text-text-primary'
                    }`}
                    style={{ backgroundColor: activeScope?.facilityId === scope.facilityId ? undefined : 'var(--bg-drawer)' }}
                  >
                    <div className="font-medium">{scope.name || scope.facilityId}</div>
                  </button>
                ))}
              </div>
            ))
          ) : (
            availableScopes.map((scope) => (
              <button
                key={scope.facilityId}
                role="option"
                aria-selected={activeScope?.facilityId === scope.facilityId}
                onClick={() => handleScopeChange(scope)}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-hover-bg ${
                  activeScope?.facilityId === scope.facilityId
                    ? 'bg-accent-blue/10 text-accent-blue'
                    : 'text-text-primary'
                }`}
                style={{ backgroundColor: activeScope?.facilityId === scope.facilityId ? undefined : 'var(--bg-drawer)' }}
              >
                <div className="font-medium">{scope.name || scope.facilityId}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
